/**
 * Keeps sibling tabs from acting on a session that ended somewhere else.
 *
 * The auth store persists to localStorage but does not observe it. Without
 * this, a logout (or a failed refresh) in one tab leaves other tabs holding
 * an in-memory session whose fund context the API can no longer resolve.
 */

import { AUTH_STORAGE_KEY, readPersistedUserId } from './session';

export type CrossTabAuthSyncDeps = {
  readCurrentUserId(): string | null;
  onSessionEnded(): void;
};

export function handleAuthStorageEvent(
  event: Pick<StorageEvent, 'key' | 'newValue'>,
  deps: CrossTabAuthSyncDeps,
): void {
  if (event.key !== null && event.key !== AUTH_STORAGE_KEY) return;

  const currentUserId = deps.readCurrentUserId();
  if (!currentUserId) return;

  // key === null means the whole store was cleared.
  const nextUserId = event.key === null ? null : readPersistedUserId(event.newValue);
  if (nextUserId === currentUserId) return;

  deps.onSessionEnded();
}

export function startCrossTabAuthSync(deps: CrossTabAuthSyncDeps): () => void {
  if (typeof window === 'undefined') return () => undefined;

  const listener = (event: StorageEvent): void => handleAuthStorageEvent(event, deps);
  window.addEventListener('storage', listener);
  return () => window.removeEventListener('storage', listener);
}
